'use client';

import { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getStripe } from '@/lib/stripe/client';
import { createClient } from '@/lib/supabase/client';
import type { PaymentIntent } from '@stripe/stripe-js';

interface FormData {
  service: string;
  date: string;
  time: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  notes: string;
  billingAddressLine1: string;
  billingCity: string;
  billingState: string;
  billingPostalCode: string;
  billingCountry: string;
}

interface FormErrors {
  service?: string;
  date?: string;
  time?: string;
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  billingAddressLine1?: string;
  billingCity?: string;
  billingState?: string;
  billingPostalCode?: string;
  billingCountry?: string;
}

interface PaymentFormProps {
  clientSecret: string;
  onSuccess: (paymentIntent: PaymentIntent) => void;
  onBack: () => void;
  amount: number;
}

// ─── Success Confirmation Modal ───────────────────────────────────────────────
interface SuccessModalProps {
  isOpen: boolean;
  formData: FormData;
  onClose: () => void;
  onNewBooking: () => void;
}

function SuccessModal({ isOpen, formData, onClose, onNewBooking }: SuccessModalProps) {
  if (!isOpen) return null;

  const serviceLabels: Record<string, string> = {
    deep: 'Deep Clean',
    standard: 'Standard Clean',
    moveinout: 'Move-In/Out',
    office: 'Office Cleaning',
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="success-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md mx-auto p-8 text-center animate-in fade-in zoom-in-95 duration-300">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Close"
        >
          <Icon name="XMarkIcon" size={20} variant="outline" />
        </button>

        {/* Success icon */}
        <div className="w-20 h-20 bg-[#4CAF50]/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <Icon name="CheckCircleIcon" size={48} variant="solid" className="text-[#4CAF50]" />
        </div>

        <h3 id="success-modal-title" className="text-2xl font-bold text-foreground mb-2">
          Booking Confirmed!
        </h3>
        <p className="text-muted-foreground text-sm mb-6">
          A confirmation email has been sent to{' '}
          <span className="font-semibold text-foreground">{formData.email}</span>
        </p>

        {/* Booking details */}
        <div className="bg-secondary/30 border border-border rounded-xl p-4 mb-6 text-left space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Service</span>
            <span className="font-medium text-foreground">{serviceLabels[formData.service] ?? formData.service}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Date</span>
            <span className="font-medium text-foreground">{formData.date}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Time</span>
            <span className="font-medium text-foreground">{formData.time}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Address</span>
            <span className="font-medium text-foreground text-right max-w-[60%]">{formData.address}</span>
          </div>
        </div>

        {/* What's next */}
        <ul className="text-sm text-muted-foreground space-y-2 text-left mb-8">
          <li className="flex items-start gap-2">
            <Icon name="CheckIcon" size={16} variant="solid" className="text-[#4CAF50] flex-shrink-0 mt-0.5" />
            <span>You&apos;ll receive a reminder 24 hours before your appointment</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="CheckIcon" size={16} variant="solid" className="text-[#4CAF50] flex-shrink-0 mt-0.5" />
            <span>Our team will call 30 minutes before arrival</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="CheckIcon" size={16} variant="solid" className="text-[#4CAF50] flex-shrink-0 mt-0.5" />
            <span>You can reschedule or cancel up to 24 hours before</span>
          </li>
        </ul>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={onClose}
            className="flex-1 border-2 border-border bg-card text-foreground py-3 rounded-lg font-semibold hover:bg-muted transition-all"
          >
            Close
          </button>
          <button
            onClick={onNewBooking}
            className="flex-1 bg-[#2E7D32] text-white py-3 rounded-lg font-semibold hover:bg-[#4CAF50] transition-all shadow-md"
          >
            Book Another
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Booking Failure Banner ───────────────────────────────────────────────────
interface BookingFailureBannerProps {
  message: string;
  onRetry: () => void;
  onDismiss: () => void;
}

function BookingFailureBanner({ message, onRetry, onDismiss }: BookingFailureBannerProps) {
  return (
    <div className="p-4 bg-destructive/10 border border-destructive rounded-xl flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex items-start gap-3 flex-1">
        <Icon name="ExclamationTriangleIcon" size={20} variant="solid" className="text-destructive flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-destructive">Booking Failed</p>
          <p className="text-sm text-destructive/80 mt-0.5">{message}</p>
        </div>
      </div>
      <div className="flex gap-2 sm:flex-shrink-0">
        <button
          type="button"
          onClick={onRetry}
          className="flex items-center gap-1.5 px-4 py-2 bg-destructive text-white text-sm font-semibold rounded-lg hover:bg-destructive/90 transition-colors"
        >
          <Icon name="ArrowPathIcon" size={14} variant="outline" />
          Retry
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="px-3 py-2 text-sm text-destructive hover:text-destructive/70 transition-colors"
          aria-label="Dismiss error"
        >
          <Icon name="XMarkIcon" size={16} variant="outline" />
        </button>
      </div>
    </div>
  );
}

// ─── Payment Form ─────────────────────────────────────────────────────────────
function PaymentForm({ clientSecret, onSuccess, onBack, amount }: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const supabase = createClient();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/homepage`,
        },
        redirect: 'if_required',
      });

      if (error) {
        setErrorMessage(error.message || 'Payment failed. Please try again.');
        setIsProcessing(false);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        const { error: confirmError } = await supabase.functions.invoke('confirm-payment', {
          body: { paymentIntentId: paymentIntent.id },
        });

        if (confirmError) {
          console.error('Backend confirmation failed:', confirmError);
          setErrorMessage('Payment succeeded but confirmation failed. Please contact support.');
          setIsProcessing(false);
          return;
        }

        onSuccess(paymentIntent);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setErrorMessage('An unexpected error occurred. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-card border border-border rounded-xl p-8 shadow-lg">
      <div className="space-y-6">
        <div className="flex items-center gap-3 mb-6">
          <Icon name="LockClosedIcon" size={24} variant="solid" className="text-primary" />
          <p className="text-sm text-muted-foreground">Your payment information is secure and encrypted</p>
        </div>

        <PaymentElement />

        {errorMessage && (
          <div className="p-4 bg-destructive/10 border border-destructive rounded-lg">
            <p className="text-sm text-destructive flex items-center gap-2">
              <Icon name="ExclamationCircleIcon" size={16} variant="solid" />
              {errorMessage}
            </p>
          </div>
        )}

        <div className="flex gap-4 pt-4">
          <button
            type="button"
            onClick={onBack}
            disabled={isProcessing}
            className="w-full border-2 border-border bg-card text-foreground py-4 rounded-lg font-semibold hover:bg-muted transition-all active:scale-95 active:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={!stripe || isProcessing}
            className="w-full bg-primary text-primary-foreground py-4 rounded-lg font-semibold hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 active:shadow-sm"
          >
            {isProcessing ? (
              <>
                <Icon name="ArrowPathIcon" size={20} variant="outline" className="animate-spin" />
                Processing...
              </>
            ) : (
              <>
                Pay ${amount}
                <Icon name="CheckCircleIcon" size={20} variant="solid" />
              </>
            )}
          </button>
        </div>

        <p className="text-xs text-muted-foreground text-center pt-4">
          By completing this booking, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </form>
  );
}

// ─── Main Booking Section ─────────────────────────────────────────────────────
export default function BookingSection() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [step, setStep] = useState<'booking' | 'payment' | 'success'>('booking');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [quotePreFilled, setQuotePreFilled] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    service: '',
    date: '',
    time: '',
    name: '',
    email: '',
    phone: '',
    address: '',
    notes: '',
    billingAddressLine1: '',
    billingCity: '',
    billingState: '',
    billingPostalCode: '',
    billingCountry: 'US',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState<{ type: string; value: number; code: string } | null>(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);

  const supabase = createClient();

  // Auto-populate form from quote URL params
  useEffect(() => {
    const serviceParam = searchParams.get('service');
    const addressParam = searchParams.get('address');

    if (serviceParam) {
      // Map instant-quote service keys to booking form service keys (they match)
      const validServices = ['standard', 'deep', 'moveinout', 'office'];
      const service = validServices.includes(serviceParam) ? serviceParam : '';

      setFormData(prev => ({
        ...prev,
        ...(service ? { service } : {}),
        ...(addressParam ? { address: addressParam } : {}),
      }));
      setQuotePreFilled(true);
    }
  }, [searchParams]);

  const getServiceAmount = (service: string): number => {
    switch (service) {
      case 'deep': return 349;
      case 'standard': return 199;
      case 'moveinout': return 469;
      case 'office': return 249;
      default: return 0;
    }
  };

  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case 'service':
        return !value ? 'Please select a service' : undefined;
      case 'date':
        if (!value) return 'Please select a date';
        const selectedDate = new Date(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return selectedDate < today ? 'Date cannot be in the past' : undefined;
      case 'time':
        return !value ? 'Please select a time' : undefined;
      case 'name':
        if (!value) return 'Name is required';
        return value.trim().length < 2 ? 'Name must be at least 2 characters' : undefined;
      case 'email':
        if (!value) return 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return !emailRegex.test(value) ? 'Please enter a valid email address' : undefined;
      case 'phone':
        if (!value) return 'Phone number is required';
        const phoneRegex = /^[\d\s()+-]{10,}$/;
        return !phoneRegex.test(value) ? 'Please enter a valid phone number' : undefined;
      case 'address':
        if (!value) return 'Service address is required';
        return value.trim().length < 5 ? 'Please enter a complete address' : undefined;
      case 'billingAddressLine1':
        if (!value) return 'Billing address is required';
        return value.trim().length < 5 ? 'Please enter a complete address' : undefined;
      case 'billingCity':
        if (!value) return 'City is required';
        return value.trim().length < 2 ? 'Please enter a valid city' : undefined;
      case 'billingState':
        if (!value) return 'State is required';
        return value.trim().length < 2 ? 'Please enter a valid state' : undefined;
      case 'billingPostalCode':
        if (!value) return 'Postal code is required';
        const zipRegex = /^\d{5}(-\d{4})?$/;
        return !zipRegex.test(value) ? 'Please enter a valid ZIP code' : undefined;
      default:
        return undefined;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (touchedFields.has(name)) {
      const error = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setTouchedFields(prev => new Set(prev).add(name));
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBookingError(null);

    const bookingFields = ['service', 'date', 'time', 'name', 'email', 'phone', 'address', 'billingAddressLine1', 'billingCity', 'billingState', 'billingPostalCode'];
    const newErrors: FormErrors = {};
    let hasErrors = false;

    bookingFields.forEach(field => {
      const error = validateField(field, formData[field as keyof FormData]);
      if (error) {
        newErrors[field as keyof FormErrors] = error;
        hasErrors = true;
      }
    });

    setTouchedFields(new Set(bookingFields));
    setErrors(newErrors);

    if (hasErrors) {
      // Scroll to first error
      setTimeout(() => {
        const firstError = document.querySelector('[data-field-error]');
        firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }

    setIsSubmitting(true);

    try {
      const amount = getServiceAmount(formData.service);

      const { data, error } = await supabase.functions.invoke<{ clientSecret: string; bookingId: string }>('create-payment-intent', {
        body: {
          service: formData.service,
          date: formData.date,
          time: formData.time,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          notes: formData.notes,
          amount,
          billing: {
            address_line_1: formData.billingAddressLine1,
            city: formData.billingCity,
            state: formData.billingState,
            postal_code: formData.billingPostalCode,
            country: formData.billingCountry,
          },
        },
      });

      if (error) {
        console.error('Payment intent creation failed:', error);
        setBookingError((data as { error?: string })?.error || error.message || 'Failed to initialize payment. Please try again.');
        setIsSubmitting(false);
        return;
      }

      if (data?.clientSecret) {
        setClientSecret(data.clientSecret);
        setStep('payment');
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setBookingError('An unexpected error occurred. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentSuccess = (paymentIntent: PaymentIntent) => {
    setShowSuccessModal(true);
    setStep('success');
    router.push(`/thank-you?payment_intent=${paymentIntent.id}`);
  };

  const handleBackToBooking = () => {
    setStep('booking');
    setClientSecret(null);
    setBookingError(null);
  };

  const handleNewBooking = () => {
    setShowSuccessModal(false);
    setStep('booking');
    setClientSecret(null);
    setBookingError(null);
    setFormData({
      service: '',
      date: '',
      time: '',
      name: '',
      email: '',
      phone: '',
      address: '',
      notes: '',
      billingAddressLine1: '',
      billingCity: '',
      billingState: '',
      billingPostalCode: '',
      billingCountry: 'US',
    });
    setErrors({});
    setTouchedFields(new Set());
  };

  const getInputClassName = (fieldName: string, baseClassName: string) => {
    const hasError = touchedFields.has(fieldName) && errors[fieldName as keyof FormErrors];
    return `${baseClassName} ${hasError ? 'border-destructive focus:ring-destructive' : ''}`;
  };

  const stripePromise = getStripe();

  const applyPromoCode = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoError(null);
    try {
      const { data, error } = await supabase
        .from('promo_codes')
        .select('*')
        .eq('code', promoCode.toUpperCase())
        .eq('is_active', true)
        .single();

      if (error || !data) {
        setPromoError('Invalid or expired promo code');
        setPromoDiscount(null);
        return;
      }
      if (data.valid_until && new Date(data.valid_until) < new Date()) {
        setPromoError('This promo code has expired');
        setPromoDiscount(null);
        return;
      }
      if (data.max_uses && data.current_uses >= data.max_uses) {
        setPromoError('This promo code has reached its usage limit');
        setPromoDiscount(null);
        return;
      }
      setPromoDiscount({ type: data.discount_type, value: parseFloat(data.discount_value), code: data.code });
      setPromoError(null);
    } catch (err) {
      console.error('Promo code error:', err);
      setPromoError('Failed to apply promo code');
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromoCode = () => {
    setPromoCode('');
    setPromoDiscount(null);
    setPromoError(null);
  };

  const calculateFinalAmount = () => {
    const baseAmount = getServiceAmount(formData.service);
    if (!promoDiscount) return baseAmount;
    if (promoDiscount.type === 'percentage') {
      return baseAmount - (baseAmount * promoDiscount.value / 100);
    } else {
      return Math.max(0, baseAmount - promoDiscount.value);
    }
  };

  // Count total validation errors for summary banner
  const errorCount = Object.values(errors).filter(Boolean).length;

  return (
    <>
      {/* Success Confirmation Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        formData={formData}
        onClose={() => setShowSuccessModal(false)}
        onNewBooking={handleNewBooking}
      />

      <section id="booking" className="w-full bg-background py-24 lg:py-32 border-t border-border">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl">

          {/* Section Header */}
          <div className="text-center mb-12 reveal">
            <h2 className="text-4xl lg:text-5xl font-bold text-foreground tracking-tight mb-4">
              {step === 'booking' && <>Book Your Cleaning</>}
              {step === 'payment' && <>Secure Payment</>}
              {step === 'success' && <>Booking Confirmed!</>}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {step === 'booking' && <>Select your service, choose a time, and we&apos;ll handle the rest</>}
              {step === 'payment' && <>Complete your booking with secure payment processing</>}
              {step === 'success' && <>Your cleaning is scheduled. We&apos;ll send you a confirmation email shortly.</>}
            </p>
          </div>

          {/* Step Indicator */}
          {step !== 'success' && (
            <div className="flex items-center justify-center gap-4 mb-12 reveal delay-100">
              <div className={`flex items-center gap-2 ${step === 'booking' ? 'text-primary' : 'text-muted-foreground'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${step === 'booking' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                  1
                </div>
                <span className="text-sm font-medium">Booking</span>
              </div>
              <div className="w-16 h-px bg-border" />
              <div className={`flex items-center gap-2 ${step === 'payment' ? 'text-primary' : 'text-muted-foreground'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${step === 'payment' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                  2
                </div>
                <span className="text-sm font-medium">Payment</span>
              </div>
            </div>
          )}

          {/* Booking Form */}
          {step === 'booking' && (
            <form onSubmit={handleBookingSubmit} className="bg-card border border-border rounded-xl p-8 shadow-lg reveal delay-200" noValidate>
              <div className="space-y-6">

                {/* Quote pre-fill banner */}
                {quotePreFilled && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                    <Icon name="CheckCircleIcon" size={18} variant="solid" className="text-green-700 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-green-800">Quote Applied!</p>
                      <p className="text-sm text-green-700">Your service details from the instant quote have been pre-filled. Review and complete your booking below.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setQuotePreFilled(false)}
                      className="ml-auto text-green-600 hover:text-green-800 flex-shrink-0"
                      aria-label="Dismiss"
                    >
                      <Icon name="XMarkIcon" size={16} variant="outline" />
                    </button>
                  </div>
                )}

                {/* Validation summary banner */}
                {errorCount > 0 && touchedFields.size > 0 && (
                  <div className="p-4 bg-destructive/10 border border-destructive rounded-lg flex items-start gap-3">
                    <Icon name="ExclamationTriangleIcon" size={18} variant="solid" className="text-destructive flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-destructive">
                      Please fix <strong>{errorCount}</strong> {errorCount === 1 ? 'error' : 'errors'} below before continuing.
                    </p>
                  </div>
                )}

                {/* Booking failure error banner */}
                {bookingError && (
                  <BookingFailureBanner
                    message={bookingError}
                    onRetry={handleBookingSubmit as unknown as () => void}
                    onDismiss={() => setBookingError(null)}
                  />
                )}

                {/* Service Selection */}
                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-foreground mb-2">
                    Select Service *
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    aria-invalid={!!(touchedFields.has('service') && errors.service)}
                    aria-describedby={touchedFields.has('service') && errors.service ? 'service-error' : undefined}
                    className={getInputClassName('service', 'w-full px-4 py-3 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring')}
                  >
                    <option value="">Choose a service...</option>
                    <option value="deep">Deep Clean - $349 (4-6 hours)</option>
                    <option value="standard">Standard Clean - $199 (2-3 hours)</option>
                    <option value="moveinout">Move-In/Out - $469 (5-8 hours)</option>
                    <option value="office">Office Cleaning - $249 (Custom)</option>
                  </select>
                  {touchedFields.has('service') && errors.service && (
                    <p id="service-error" data-field-error className="mt-1 text-sm text-destructive flex items-center gap-1">
                      <Icon name="ExclamationCircleIcon" size={16} variant="solid" />
                      {errors.service}
                    </p>
                  )}
                </div>

                {/* Date & Time */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="date" className="block text-sm font-medium text-foreground mb-2">
                      Preferred Date *
                    </label>
                    <input
                      type="date"
                      id="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      min={new Date().toISOString().split('T')[0]}
                      aria-invalid={!!(touchedFields.has('date') && errors.date)}
                      aria-describedby={touchedFields.has('date') && errors.date ? 'date-error' : undefined}
                      className={getInputClassName('date', 'w-full px-4 py-3 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring')}
                    />
                    {touchedFields.has('date') && errors.date && (
                      <p id="date-error" data-field-error className="mt-1 text-sm text-destructive flex items-center gap-1">
                        <Icon name="ExclamationCircleIcon" size={16} variant="solid" />
                        {errors.date}
                      </p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="time" className="block text-sm font-medium text-foreground mb-2">
                      Preferred Time *
                    </label>
                    <select
                      id="time"
                      name="time"
                      value={formData.time}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      aria-invalid={!!(touchedFields.has('time') && errors.time)}
                      aria-describedby={touchedFields.has('time') && errors.time ? 'time-error' : undefined}
                      className={getInputClassName('time', 'w-full px-4 py-3 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring')}
                    >
                      <option value="">Select time...</option>
                      <option value="08:00">8:00 AM</option>
                      <option value="09:00">9:00 AM</option>
                      <option value="10:00">10:00 AM</option>
                      <option value="11:00">11:00 AM</option>
                      <option value="12:00">12:00 PM</option>
                      <option value="13:00">1:00 PM</option>
                      <option value="14:00">2:00 PM</option>
                      <option value="15:00">3:00 PM</option>
                      <option value="16:00">4:00 PM</option>
                    </select>
                    {touchedFields.has('time') && errors.time && (
                      <p id="time-error" data-field-error className="mt-1 text-sm text-destructive flex items-center gap-1">
                        <Icon name="ExclamationCircleIcon" size={16} variant="solid" />
                        {errors.time}
                      </p>
                    )}
                  </div>
                </div>

                {/* Contact Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="John Doe"
                      aria-invalid={!!(touchedFields.has('name') && errors.name)}
                      aria-describedby={touchedFields.has('name') && errors.name ? 'name-error' : undefined}
                      className={getInputClassName('name', 'w-full px-4 py-3 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring')}
                    />
                    {touchedFields.has('name') && errors.name && (
                      <p id="name-error" data-field-error className="mt-1 text-sm text-destructive flex items-center gap-1">
                        <Icon name="ExclamationCircleIcon" size={16} variant="solid" />
                        {errors.name}
                      </p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="john@example.com"
                      aria-invalid={!!(touchedFields.has('email') && errors.email)}
                      aria-describedby={touchedFields.has('email') && errors.email ? 'email-error' : undefined}
                      className={getInputClassName('email', 'w-full px-4 py-3 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring')}
                    />
                    {touchedFields.has('email') && errors.email && (
                      <p id="email-error" data-field-error className="mt-1 text-sm text-destructive flex items-center gap-1">
                        <Icon name="ExclamationCircleIcon" size={16} variant="solid" />
                        {errors.email}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="(555) 123-4567"
                    aria-invalid={!!(touchedFields.has('phone') && errors.phone)}
                    aria-describedby={touchedFields.has('phone') && errors.phone ? 'phone-error' : undefined}
                    className={getInputClassName('phone', 'w-full px-4 py-3 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring')}
                  />
                  {touchedFields.has('phone') && errors.phone && (
                    <p id="phone-error" data-field-error className="mt-1 text-sm text-destructive flex items-center gap-1">
                      <Icon name="ExclamationCircleIcon" size={16} variant="solid" />
                      {errors.phone}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-foreground mb-2">
                    Service Address *
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="123 Main St, Austin, TX 78701"
                    aria-invalid={!!(touchedFields.has('address') && errors.address)}
                    aria-describedby={touchedFields.has('address') && errors.address ? 'address-error' : undefined}
                    className={getInputClassName('address', 'w-full px-4 py-3 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring')}
                  />
                  {touchedFields.has('address') && errors.address && (
                    <p id="address-error" data-field-error className="mt-1 text-sm text-destructive flex items-center gap-1">
                      <Icon name="ExclamationCircleIcon" size={16} variant="solid" />
                      {errors.address}
                    </p>
                  )}
                </div>

                {/* Billing Address Section */}
                <div className="border-t border-border pt-6 mt-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Billing Address</h3>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="billingAddressLine1" className="block text-sm font-medium text-foreground mb-2">
                        Street Address *
                      </label>
                      <input
                        type="text"
                        id="billingAddressLine1"
                        name="billingAddressLine1"
                        value={formData.billingAddressLine1}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="123 Main St"
                        aria-invalid={!!(touchedFields.has('billingAddressLine1') && errors.billingAddressLine1)}
                        aria-describedby={touchedFields.has('billingAddressLine1') && errors.billingAddressLine1 ? 'billingAddress-error' : undefined}
                        className={getInputClassName('billingAddressLine1', 'w-full px-4 py-3 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring')}
                      />
                      {touchedFields.has('billingAddressLine1') && errors.billingAddressLine1 && (
                        <p id="billingAddress-error" data-field-error className="mt-1 text-sm text-destructive flex items-center gap-1">
                          <Icon name="ExclamationCircleIcon" size={16} variant="solid" />
                          {errors.billingAddressLine1}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="billingCity" className="block text-sm font-medium text-foreground mb-2">
                          City *
                        </label>
                        <input
                          type="text"
                          id="billingCity"
                          name="billingCity"
                          value={formData.billingCity}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          placeholder="Minneapolis"
                          aria-invalid={!!(touchedFields.has('billingCity') && errors.billingCity)}
                          aria-describedby={touchedFields.has('billingCity') && errors.billingCity ? 'billingCity-error' : undefined}
                          className={getInputClassName('billingCity', 'w-full px-4 py-3 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring')}
                        />
                        {touchedFields.has('billingCity') && errors.billingCity && (
                          <p id="billingCity-error" data-field-error className="mt-1 text-sm text-destructive flex items-center gap-1">
                            <Icon name="ExclamationCircleIcon" size={16} variant="solid" />
                            {errors.billingCity}
                          </p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="billingState" className="block text-sm font-medium text-foreground mb-2">
                          State *
                        </label>
                        <input
                          type="text"
                          id="billingState"
                          name="billingState"
                          value={formData.billingState}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          placeholder="MN"
                          aria-invalid={!!(touchedFields.has('billingState') && errors.billingState)}
                          aria-describedby={touchedFields.has('billingState') && errors.billingState ? 'billingState-error' : undefined}
                          className={getInputClassName('billingState', 'w-full px-4 py-3 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring')}
                        />
                        {touchedFields.has('billingState') && errors.billingState && (
                          <p id="billingState-error" data-field-error className="mt-1 text-sm text-destructive flex items-center gap-1">
                            <Icon name="ExclamationCircleIcon" size={16} variant="solid" />
                            {errors.billingState}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="billingPostalCode" className="block text-sm font-medium text-foreground mb-2">
                          ZIP Code *
                        </label>
                        <input
                          type="text"
                          id="billingPostalCode"
                          name="billingPostalCode"
                          value={formData.billingPostalCode}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          placeholder="55401"
                          aria-invalid={!!(touchedFields.has('billingPostalCode') && errors.billingPostalCode)}
                          aria-describedby={touchedFields.has('billingPostalCode') && errors.billingPostalCode ? 'billingZip-error' : undefined}
                          className={getInputClassName('billingPostalCode', 'w-full px-4 py-3 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring')}
                        />
                        {touchedFields.has('billingPostalCode') && errors.billingPostalCode && (
                          <p id="billingZip-error" data-field-error className="mt-1 text-sm text-destructive flex items-center gap-1">
                            <Icon name="ExclamationCircleIcon" size={16} variant="solid" />
                            {errors.billingPostalCode}
                          </p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="billingCountry" className="block text-sm font-medium text-foreground mb-2">
                          Country *
                        </label>
                        <select
                          id="billingCountry"
                          name="billingCountry"
                          value={formData.billingCountry}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        >
                          <option value="US">United States</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-foreground mb-2">
                    Special Instructions (Optional)
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="Any special requests or areas that need extra attention..."
                    className="w-full px-4 py-3 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary text-primary-foreground py-4 rounded-lg font-semibold hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 active:shadow-sm"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <Icon name="ArrowPathIcon" size={20} variant="outline" className="animate-spin" />
                      Setting up payment...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Continue to Payment
                      <Icon name="ArrowRightIcon" size={20} variant="outline" />
                    </span>
                  )}
                </button>

              </div>
            </form>
          )}

          {/* Payment Form */}
          {step === 'payment' && clientSecret && (
            <div className="space-y-6 reveal">

              {/* Booking Summary */}
              <div className="bg-secondary/30 border border-border rounded-xl p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Booking Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Service:</span>
                    <span className="font-medium text-foreground">
                      {formData.service === 'deep' && 'Deep Clean'}
                      {formData.service === 'standard' && 'Standard Clean'}
                      {formData.service === 'moveinout' && 'Move-In/Out'}
                      {formData.service === 'office' && 'Office Cleaning'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Date & Time:</span>
                    <span className="font-medium text-foreground">{formData.date} at {formData.time}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Address:</span>
                    <span className="font-medium text-foreground">{formData.address}</span>
                  </div>
                  <div className="border-t border-border pt-3 mt-3 flex justify-between text-base">
                    <span className="text-foreground font-semibold">Total:</span>
                    <span className="text-primary font-bold text-xl">
                      ${getServiceAmount(formData.service)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Promo Code Section */}
              <div className="bg-green-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="TicketIcon" size={20} className="text-green-600" />
                  Have a Promo Code?
                </h3>
                {!promoDiscount ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      placeholder="Enter promo code"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      disabled={promoLoading}
                    />
                    <button
                      type="button"
                      onClick={applyPromoCode}
                      disabled={promoLoading || !promoCode.trim()}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {promoLoading ? 'Checking...' : 'Apply'}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-white p-4 rounded-lg border-2 border-green-500">
                    <div className="flex items-center gap-3">
                      <Icon name="CheckCircleIcon" size={24} className="text-green-600" variant="solid" />
                      <div>
                        <p className="font-semibold text-foreground">{promoDiscount.code} Applied!</p>
                        <p className="text-sm text-muted-foreground">
                          {promoDiscount.type === 'percentage'
                            ? `${promoDiscount.value}% discount`
                            : `$${promoDiscount.value.toFixed(2)} off`}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={removePromoCode}
                      className="text-red-600 hover:text-red-700 font-semibold text-sm"
                    >
                      Remove
                    </button>
                  </div>
                )}
                {promoError && (
                  <p className="mt-2 text-sm text-red-600">{promoError}</p>
                )}
              </div>

              {/* Price Summary */}
              <div className="bg-gray-50 p-6 rounded-xl">
                <div className="space-y-3">
                  <div className="flex justify-between text-foreground">
                    <span>Service Price:</span>
                    <span className="font-semibold">${getServiceAmount(formData.service).toFixed(2)}</span>
                  </div>
                  {promoDiscount && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount ({promoDiscount.code}):</span>
                      <span className="font-semibold">
                        -{promoDiscount.type === 'percentage'
                          ? `$${(getServiceAmount(formData.service) * promoDiscount.value / 100).toFixed(2)}`
                          : `$${promoDiscount.value.toFixed(2)}`}
                      </span>
                    </div>
                  )}
                  <div className="border-t border-gray-300 pt-3 flex justify-between text-lg font-bold text-foreground">
                    <span>Total:</span>
                    <span className="text-green-600">${calculateFinalAmount().toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Stripe Payment Form */}
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <PaymentForm
                  clientSecret={clientSecret}
                  onSuccess={handlePaymentSuccess}
                  onBack={handleBackToBooking}
                  amount={calculateFinalAmount()}
                />
              </Elements>
            </div>
          )}

          {/* Success Message (inline fallback) */}
          {step === 'success' && (
            <div className="bg-card border border-border rounded-xl p-12 shadow-lg text-center reveal">
              <div className="w-20 h-20 bg-[#4CAF50]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="CheckCircleIcon" size={48} variant="solid" className="text-[#4CAF50]" />
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-4">Booking Confirmed!</h3>
              <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                We&apos;ve sent a confirmation email to{' '}
                <span className="font-medium text-foreground">{formData.email}</span> with all the details.
              </p>
              <button
                onClick={handleNewBooking}
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 rounded-lg font-semibold hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl active:scale-95 active:shadow-sm"
              >
                Book Another Cleaning
                <Icon name="PlusCircleIcon" size={20} variant="solid" />
              </button>
            </div>
          )}

        </div>
      </section>
    </>
  );
}